/**
 * MQLLibraryDropdown Component (P9-C)
 * Dropdown to select from user's MQL Code Library
 *
 * Features:
 * - Groups files by type (EA, Indicator, Script)
 * - Shows quality score and line count
 * - Instant load from existing analysis (0 tokens)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Menu,
    MenuItem,
    ListSubheader,
    Divider,
    CircularProgress,
    Chip,
    Tooltip
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarIcon from '@mui/icons-material/Star';
import CodeIcon from '@mui/icons-material/Code';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TerminalIcon from '@mui/icons-material/Terminal';
import BoltIcon from '@mui/icons-material/Bolt';

/**
 * Get icon for file type
 * @param {string} fileType - EA | Indicator | Script
 * @returns {JSX.Element} - Icon component
 */
const getFileTypeIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    if (type === 'ea' || type === 'expert') {
        return <CodeIcon sx={{ fontSize: '1rem' }} />;
    }
    if (type === 'indicator') {
        return <ShowChartIcon sx={{ fontSize: '1rem' }} />;
    }
    if (type === 'script') {
        return <TerminalIcon sx={{ fontSize: '1rem' }} />;
    }
    return <CodeIcon sx={{ fontSize: '1rem' }} />;
};

/**
 * Get color for file type
 * @param {string} fileType - EA | Indicator | Script
 * @returns {string} - Hex color
 */
const getFileTypeColor = (fileType) => {
    const type = fileType?.toLowerCase();
    if (type === 'ea' || type === 'expert') return '#7c3aed';
    if (type === 'indicator') return '#3B82F6';
    if (type === 'script') return '#F59E0B';
    return '#7c3aed';
};

/**
 * Get quality score color
 * @param {number} score - Quality score (0-10)
 * @returns {string} - Hex color
 */
const getScoreColor = (score) => {
    if (score >= 8) return '#27B56D';
    if (score >= 6) return '#F59E0B';
    if (score >= 4) return '#EF4444';
    return '#6B7280';
};

/**
 * MQLLibraryDropdown Component
 * @param {Array} libraryItems - Array of library items from API
 * @param {object} grouped - Items grouped by type { EA: [...], Indicator: [...] }
 * @param {object} byType - Count by type { ea: 5, indicator: 2 }
 * @param {function} onSelect - Handler when item is selected
 * @param {boolean} isLoading - Loading state
 * @param {string} error - Error message
 * @param {function} onRefresh - Handler to refresh library
 * @param {boolean} disabled - Disable dropdown
 */
const MQLLibraryDropdown = ({
    libraryItems = [],
    grouped = {},
    byType = {},
    onSelect,
    isLoading = false,
    error = null,
    onRefresh,
    disabled = false
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const totalCount = Object.values(byType).reduce((sum, count) => sum + count, 0);
    const hasItems = totalCount > 0;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        // Refresh on open if we have a refresh handler
        if (onRefresh && !isLoading) {
            onRefresh();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (item) => {
        handleClose();
        if (onSelect) {
            onSelect(item);
        }
    };

    /**
     * Render grouped menu items
     */
    const renderGroupedItems = () => {
        const typeOrder = ['EA', 'Indicator', 'Script'];
        const items = [];

        typeOrder.forEach((type, index) => {
            const typeItems = grouped[type] || [];
            if (typeItems.length === 0) return;

            // Add divider between groups (not before first)
            if (items.length > 0) {
                items.push(<Divider key={`divider-${type}`} sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />);
            }

            // Group header
            items.push(
                <ListSubheader
                    key={`header-${type}`}
                    sx={{
                        backgroundColor: '#0F0F14',
                        color: getFileTypeColor(type),
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        lineHeight: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    {getFileTypeIcon(type)}
                    {type}s ({typeItems.length})
                </ListSubheader>
            );

            // Items in group
            typeItems.forEach((item) => {
                const scoreColor = getScoreColor(item.qualityScore);

                items.push(
                    <MenuItem
                        key={item.fileId || item.name}
                        onClick={() => handleSelect(item)}
                        sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                            {/* Serial Number Badge */}
                            <Box
                                sx={{
                                    minWidth: 48,
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: `${getFileTypeColor(item.fileType)}15`,
                                    border: `1px solid ${getFileTypeColor(item.fileType)}30`,
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: getFileTypeColor(item.fileType),
                                        fontSize: '0.6875rem',
                                        fontWeight: 700,
                                        fontFamily: "'JetBrains Mono', monospace"
                                    }}
                                >
                                    {item.displayLabel?.split(':')[0] || `#${item.serialNo}`}
                                </Typography>
                            </Box>

                            {/* File Name */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: '#FFFFFF',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        fontFamily: "'JetBrains Mono', monospace",
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {item.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    {item.lineCount?.toLocaleString() || '?'} lines
                                </Typography>
                            </Box>

                            {/* Quality Score */}
                            {item.qualityScore !== null && item.qualityScore !== undefined && (
                                <Tooltip title="Quality Score" arrow placement="top">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <StarIcon sx={{ color: scoreColor, fontSize: '0.875rem' }} />
                                        <Typography
                                            sx={{
                                                color: scoreColor,
                                                fontSize: '0.8125rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {item.qualityScore.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            )}

                            {/* Instant Badge */}
                            <Tooltip title="Load instantly (0 tokens)" arrow placement="top">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(39, 181, 109, 0.15)',
                                        border: '1px solid rgba(39, 181, 109, 0.3)'
                                    }}
                                >
                                    <BoltIcon sx={{ color: '#27B56D', fontSize: '0.75rem' }} />
                                    <Typography
                                        sx={{
                                            color: '#27B56D',
                                            fontSize: '0.6875rem',
                                            fontWeight: 600
                                        }}
                                    >
                                        Instant
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    </MenuItem>
                );
            });
        });

        return items;
    };

    return (
        <Box>
            {/* Dropdown Button */}
            <Button
                onClick={handleClick}
                disabled={disabled || isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : <FolderOpenIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 16px',
                    borderRadius: '10px',
                    fontSize: '0.875rem',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        color: 'rgba(255, 255, 255, 0.3)'
                    }
                }}
            >
                Select from MQL Code Library
                {hasItems && (
                    <Chip
                        label={totalCount}
                        size="small"
                        sx={{
                            ml: 1,
                            height: 20,
                            minWidth: 24,
                            backgroundColor: 'rgba(124, 58, 237, 0.2)',
                            color: '#a855f7',
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            '& .MuiChip-label': {
                                px: 1
                            }
                        }}
                    />
                )}
            </Button>

            {/* Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        backgroundColor: '#0F0F14',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        minWidth: 380,
                        maxWidth: 480,
                        maxHeight: 400,
                        overflow: 'auto',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                        '&::-webkit-scrollbar': {
                            width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {/* Loading State */}
                {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 2, fontSize: '0.875rem' }}>
                            Loading library...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {!isLoading && error && (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: '#EF4444', fontSize: '0.875rem', mb: 1 }}>
                            {error}
                        </Typography>
                        {onRefresh && (
                            <Button
                                onClick={onRefresh}
                                size="small"
                                sx={{ color: '#a855f7', textTransform: 'none' }}
                            >
                                Try Again
                            </Button>
                        )}
                    </Box>
                )}

                {/* Empty State */}
                {!isLoading && !error && !hasItems && (
                    <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                        <FolderOpenIcon sx={{ color: 'rgba(255, 255, 255, 0.2)', fontSize: '2.5rem', mb: 1 }} />
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>
                            No analyzed EAs yet
                        </Typography>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.8125rem', mt: 0.5 }}>
                            Analyze an EA to add it to your library
                        </Typography>
                    </Box>
                )}

                {/* Library Items */}
                {!isLoading && !error && hasItems && renderGroupedItems()}
            </Menu>
        </Box>
    );
};

export default MQLLibraryDropdown;
